import { useState, useEffect } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useNavigate } from "react-router-dom";
import { Trophy, Clock, ChevronRight } from "lucide-react";
import { useFb, formatMatchDay, useCountdown } from "../../utils/football";

const FINISHED = ["FT", "AET", "PEN"];
const LIVE = ["1H", "HT", "2H", "ET", "BT", "P", "SUSP", "INT", "LIVE"];

const statusLabel = (fx) => {
  if (FINISHED.includes(fx.status)) return "Full Time";
  if (LIVE.includes(fx.status)) return "LIVE";
  return "Upcoming";
};

const MatchRow = ({ fx, onClick }) => {
  const live = LIVE.includes(fx.status);
  const finished = FINISHED.includes(fx.status);
  const showScore = live || finished;
  return (
    <button onClick={() => onClick(fx)} className="w-full rounded-xl border border-border bg-card p-3.5 text-left transition-colors hover:border-amber-400/40 active:bg-secondary/40" data-testid={`match-row-${fx.id}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-bold text-muted-foreground">{formatMatchDay(fx.match_day)}</span>
        <span className={`flex items-center rounded-full px-2 py-0.5 text-[10px] font-extrabold ${live ? "bg-red-500/20 text-red-400" : finished ? "bg-secondary text-muted-foreground" : "wc-bg-gold text-slate-900"}`}>
          {live && <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-red-400" />}
          {statusLabel(fx)}
        </span>
      </div>
      <div className="flex items-center justify-between text-sm font-bold">
        <span className="truncate">{fx.home_team?.name || "TBD"}</span>
        <span className="wc-gold">{showScore ? (fx.home_score ?? 0) : ""}</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-sm font-bold">
        <span className="truncate">{fx.away_team?.name || "TBD"}</span>
        <span className="wc-gold">{showScore ? (fx.away_score ?? 0) : ""}</span>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{!showScore && fx.kickoff_utc ? `Kickoff ${new Date(fx.kickoff_utc).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : (fx.venue || "")}</span>
        <ChevronRight size={14} />
      </div>
    </button>
  );
};

export default function FootballMatchesPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const d = await fb.get("/football/fixtures"); setFixtures(d.fixtures || []); } catch { /* ignore */ }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tapping any fixture opens its detail page (teams, kickoff, venue, lineups, live
  // scores, top performers). The squad builder is reachable FROM there via an explicit
  // "Build your squad for this match day" CTA.
  const openFixture = (fx) => navigate(`/football/fixtures/${fx.id}`);

  const upcoming = fixtures.filter((f) => !FINISHED.includes(f.status) && !LIVE.includes(f.status));
  const live = fixtures.filter((f) => LIVE.includes(f.status));
  const completed = fixtures.filter((f) => FINISHED.includes(f.status)).reverse();
  const nextKickoff = upcoming[0]?.kickoff_utc;
  const cd = useCountdown(nextKickoff);

  const TABS = [["upcoming", `Upcoming${live.length ? " · Live" : ""}`], ["completed", "Completed"]];
  const visible = tab === "upcoming" ? [...live, ...upcoming] : completed;

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="football-matches-page">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <div className="flex items-center gap-2 text-amber-300"><Trophy size={18} /><span className="text-sm font-bold">Football World Cup 2026</span></div>
        <h1 className="mt-1 text-xl font-extrabold text-white">Matches</h1>
        {nextKickoff && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-black/30 px-3 py-1.5 text-sm font-bold text-white" data-testid="matches-countdown">
            <Clock size={15} className="text-amber-300" /> Next kickoff in {cd.label}
          </div>
        )}
      </div>

      <div className="mx-auto max-w-md px-4">
        <div className="mt-4 flex gap-2 rounded-xl border border-border bg-card p-1">
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} className={`flex-1 rounded-lg py-2 text-sm font-bold ${tab === key ? "wc-bg-gold text-slate-900" : "text-muted-foreground"}`} data-testid={`matches-tab-${key}`}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="mt-5 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-secondary/50" />)}</div>
        ) : visible.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="matches-empty">
            {tab === "upcoming" ? "No upcoming matches scheduled." : "No completed matches yet."}
          </div>
        ) : (
          <div className="mt-4 space-y-2">{visible.map((fx) => <MatchRow key={fx.id} fx={fx} onClick={openFixture} />)}</div>
        )}
      </div>
    </div>
  );
}
